import * as React from "react";

import { FIRST_STRONG_EXAMPLE, SECOND_STRONG_EXAMPLE, conjunctionsForActivityType } from "../../../../constants/evidence";
import useFocus from '../../../Shared/hooks/useFocus';
import { BECAUSE, BUT, SO, safeParseHtml } from '../../../Shared/index';
import { PromptInterface } from "../../../Staff/interfaces/evidenceInterfaces";
import { trailingConjunctionPattern } from '../../libs/stringFormatting';

const ALL_CONJUNCTIONS = [BECAUSE, BUT, SO];

interface PostActivitySlideProps {
  activityType?: string,
  handleClick: () => void,
  previewMode: boolean,
  prompts: PromptInterface[],
  responses: any
}

export function getResponsesForConjunction(responses: Record<string, any[]>, conjunction: string, activeConjunctions: string[] = ALL_CONJUNCTIONS) {
  const targetConjunctionRegex = new RegExp(`\\s${conjunction}\\s`);

  const otherConjunctionRegexes =
    activeConjunctions
      .filter(conj => conj !== conjunction)
      .map(otherConjunction => new RegExp(`\\s${otherConjunction}\\s`))


  return (
    Object
      .values(responses)
      .find((responseArray) => {
        if (!responseArray?.length) return false;

        const { entry } = responseArray[responseArray.length - 1] || {};
        if (typeof entry !== "string" || !targetConjunctionRegex.test(entry)) return false;

        const conjunctionMatch = entry.match(targetConjunctionRegex);
        if (conjunctionMatch?.index == null) return false;
        const conjunctionPosition = conjunctionMatch.index;

        const hasOtherConjunction = otherConjunctionRegexes.find(otherConjunctionRegex => {
          const otherMatch = entry.match(otherConjunctionRegex);
          return otherMatch && otherMatch.index < conjunctionPosition
        });


        if (!hasOtherConjunction) { return true; }

        return false;
      }) || null
  );
}

export const PostActivitySlide = ({ activityType, handleClick, previewMode, prompts, responses }: PostActivitySlideProps) => {
  const [containerRef, setContainerFocus] = useFocus()

  React.useEffect(() => {
    setContainerFocus()
  }, [])

  const activeConjunctions = conjunctionsForActivityType(activityType)
    .filter(c => prompts.some(p => p.conjunction === c))

  function getStrongExemplar(conjunction: string, property: string) {
    const prompt = prompts.filter(prompt => prompt.conjunction === conjunction)[0];
    if (!prompt) { return }
    const stemWithoutConjunction = prompt.text.replace(new RegExp(`\\s*${trailingConjunctionPattern(conjunction)}`), '$2');
    const exemplar = prompt[property];
    return <p>{safeParseHtml(stemWithoutConjunction)}<strong>{` ${conjunction} `}</strong><br /><u>{exemplar}</u></p>;
  }

  function getResponse(conjunction: string) {
    const responseData = getResponsesForConjunction(responses, conjunction, activeConjunctions)
    if (!responseData) { return }
    const lastResponseText = responseData[responseData.length - 1].entry;
    const conjunctionRegex = new RegExp(`\\s${conjunction}\\s`)
    const splitResponse = lastResponseText.split(conjunctionRegex);
    const afterConjunction = splitResponse.slice(1).join(` ${conjunction} `);
    return <p>{splitResponse[0]}<strong>{` ${conjunction} `}</strong><br /><u>{afterConjunction}</u></p>
  }

  function renderResponseAndExamplarsSection(conjunction: string) {
    const responseId = `your-response-label-${conjunction}`;
    const exemplarsId = `example-responses-label-${conjunction}`;

    return (
      <section className="response-exemplars-section">
        <section aria-labelledby={responseId} className="response-section">
          <div className="response-exemplar-header">
            <p className="sub-header-text" id={responseId}>Your response</p>
          </div>
          <div className="response-box">{getResponse(conjunction)}</div>
        </section>
        <section aria-labelledby={exemplarsId} className="exemplars-section">
          <div className="response-exemplar-header">
            <p className="sub-header-text" id={exemplarsId}>Example strong responses</p>
          </div>
          <ul className="exemplars-list">
            <li className="exemplar-box">{getStrongExemplar(conjunction, FIRST_STRONG_EXAMPLE)}</li>
            <li className="exemplar-box">{getStrongExemplar(conjunction, SECOND_STRONG_EXAMPLE)}</li>
          </ul>
        </section>
      </section>
    );
  }
  let buttonClass = "quill-button-archived large secondary outlined focus-on-dark"
  if (previewMode) {
    buttonClass += " disabled"
  }

  return (
    <div className="post-activity-slide-container no-focus-outline" ref={containerRef} tabIndex={-1}>
      <section id="information-section">
        <img alt="An illustration of a party popper" id="celebration-vector" src={`${window.postbuildEnv.CDN_URL}/images/evidence/party-celebration.svg`} />
        <p id="revision-text">You have completed the activity!</p>
        <p className="slide-sub-text" id="second-sub-text">Be proud of the work you did today, and celebrate your success! This practice will help you grow as a reader and a writer. The more you practice, the stronger your critical thinking, reading, and writing skills will be.</p>
      </section>
      <section className="responses-exemplars-container">
        <section className="review-response-header-section">
          <p className="sub-header-text">Here are some examples of strong responses</p>
          <p className="sub-header-subtext">There are many different ways to use evidence in your writing.</p>
        </section>
        {activeConjunctions.map(c => <React.Fragment key={c}>{renderResponseAndExamplarsSection(c)}</React.Fragment>)}
      </section>
      <section id="button-container">
        <button className={buttonClass} disabled={previewMode} onClick={handleClick} type="button">Next</button>
      </section>
    </div>
  );
}

export default PostActivitySlide;
